// Copyright (c) Microsoft. All rights reserved. Licensed under the MIT license.

import { Exception } from 'common/exceptions/Exception';
import { Dialog } from './dialog';
import { getLoginParams, IEndpointConfiguration } from './endpoint';
import { ICode, IError, IToken } from './token';

/**
 * Custom error type to handle OAuth specific errors.
 */
export class AuthError extends Exception {
  constructor(message: string, public innerError?: Error) {
    super(message, innerError);
  }
}

/**
 * Helper for performing Implicit OAuth Authentication with registered endpoints.
 */
export class Authenticator {
  constructor(private endpoint: IEndpointConfiguration) {}

  authenticate(): Promise<ICode> {
    return this._openAuthDialog();
  }

  /**
   * Check if the current url is running inside of a Dialog that contains an access_token, code, or error.
   * If true then it calls messageParent by extracting the token information, thereby closing the dialog.
   * Otherwise, the caller should proceed with normal initialization of their application.
   *
   * This logic assumes that the redirect url is your application and hence when your code runs again in
   * the dialog, this logic takes over and closes it for you.
   *
   * @return Returns false if the code is running inside of a dialog without the required information
   * or is not running inside of a dialog at all.
   */
  static isAuthDialog(): boolean {
    // If the url doesn't contain an access_token, code, or error then return false.
    // This is in scenarios where we don't want to automatically control what happens to the dialog.
    if (!/(access_token|code|error|state)/gi.test(location.href)) {
      return false;
    }

    Dialog.close(location.href);
    return true;
  }

  /**
   * Extract the token from the URL
   *
   * @param url The url to extract the token from.
   * @param exclude Exclude a particular string from the url, such as a query param or specific substring.
   * @param delimiter[optional] Delimiter used by OAuth provider to mark the beginning of token response. Defaults to #.
   * @return Returns the extracted token.
   */
  private static getUrlParams(
    url: string = location.href,
    exclude: string = location.origin,
    delimiter = '#'
  ): ICode | IToken | IError {
    if (exclude) {
      url = url.replace(exclude, '');
    }

    const [left, right] = url.split(delimiter);
    let tokenString = right == null ? left : right;

    if (tokenString.indexOf('?') !== -1) {
      tokenString = tokenString.split('?')[1];
    }

    return Authenticator.extractParams(tokenString);
  }

  private static extractParams(segment: string): ICode | IToken | IError {
    if (segment == null || segment.trim() === '') {
      return null;
    }

    const params: Partial<ICode | IToken | IError> = {};
    const regex = /([^&=]+)=([^&]*)/g;
    let matchParts;

    while ((matchParts = regex.exec(segment)) !== null) {
      // Fixes bugs when the state parameters contains a / before them
      if (matchParts[1] === '/state') {
        matchParts[1] = matchParts[1].replace('/', '');
      }
      params[decodeURIComponent(matchParts[1])] = decodeURIComponent(
        matchParts[2]
      );
    }

    return params as ICode | IToken | IError;
  }

  private async _openAuthDialog(): Promise<ICode> {
    // Set the authentication state to redirect and begin the auth flow.
    const { state, url } = getLoginParams(this.endpoint);

    // Launch the dialog and perform the OAuth flow. We launch the dialog at the redirect
    // url where we expect the call to isAuthDialog to be available.
    const redirectUrl = await new Dialog<string>(url, 1024, 768).result;

    // Try and extract the result and pass it along.
    return this._handleTokenResult(redirectUrl, this.endpoint, state);
  }

  private _handleTokenResult(
    redirectUrl: string,
    endpoint: IEndpointConfiguration,
    state: number
  ) {
    const result = Authenticator.getUrlParams(
      redirectUrl,
      endpoint.redirectUrl
    );
    if (result == null) {
      throw new AuthError('No access_token or code could be parsed.');
    } else if (endpoint.state && +result.state !== state) {
      throw new AuthError("State couldn't be verified");
    } else if ('code' in result) {
      return result;
    } else {
      throw new AuthError((result as IError).error);
    }
  }
}
